import { SentryWrapper } from "@app/init/sentry/SentryWrapper";
import * as Sentry from "@sentry/browser";
import { InternalInformationSentryError, KNOWN_SENTRY_ERRORS, KnownSentryError } from "./SentryErrors";
import { SentryInternalInformationDisconnect } from "./SentryInternalInformationDisconnect";

export class BasicSentryErrorsParser {

	public parseError(event: Sentry.ErrorEvent, hint: Sentry.EventHint) {
		const exception = hint.originalException;

		let errorMessage: string | undefined;
		if (exception instanceof Error) {
			errorMessage = exception.message;
		} if (exception instanceof ErrorEvent) {
			errorMessage = exception?.error?.message || exception.message;
		} else if (typeof exception === "string") {
			errorMessage = exception;
		}

		if (errorMessage) {
			event.fingerprint = this.getFingerprintForMessage(errorMessage);
			SentryWrapper.setTag("Original error message", errorMessage);
		}

		return event;
	}

	public getFingerprintForMessage(errorMessage: string): string[] {
		const knownError = this._findKnownError(errorMessage);
		if (knownError) {
			return [this._attachKnownErrorSuffix(errorMessage, knownError)];
		}

		return [this._generateFingerprint(errorMessage)];
	}

	private _findKnownError(errorMessage: string): KnownSentryError | InternalInformationSentryError {
		if (!errorMessage || typeof errorMessage !== "string") {
			return null;
		}
		errorMessage = errorMessage.toLowerCase();

		if (errorMessage === SentryInternalInformationDisconnect.MESSAGE.toLowerCase()) {
			return SentryInternalInformationDisconnect.createInternalInformationError();
		}

		const knownError = KNOWN_SENTRY_ERRORS.find(error => {
			const foundMessage = error.messages.find(message => {
				return errorMessage.indexOf(message.toLowerCase()) !== -1;
			});
			return foundMessage;
		});
		return knownError;
	}

	private _attachKnownErrorSuffix(errorMessage: string, knownError: KnownSentryError | InternalInformationSentryError) {
		const { suffix } = knownError;
		if (!suffix?.length) {
			return knownError.fingerprint;
		}

		let fingerprint = knownError.fingerprint;
		suffix.forEach(suffixFuncOrRegex => {
			let suffix: string | undefined;
			if (typeof suffixFuncOrRegex === "function") {
				suffix = suffixFuncOrRegex(errorMessage);
			} else {
				const matches = errorMessage.match(suffixFuncOrRegex);
				suffix = matches.slice(1).filter(value => !!value).join("-");
			}

			if (suffix) {
				fingerprint += "-" + suffix;
			}
		});

		return fingerprint;
	}

	private _generateFingerprint(errorMessage: string): string {
		errorMessage = errorMessage.replace(/\d+/gm, "<number>");
		const regexPatters: Array<{ pattern: RegExp, generateFingerprint: (matches: RegExpMatchArray) => string; }> = [
			{
				pattern: /(.*) is not an object \(evaluating ['"](.*)['"]\)/gmi,
				generateFingerprint: (matches) => `cannot-read-property-${matches[2].split(".").slice(-1)}-of-${matches[1]}`
			},
			{
				pattern: /Cannot read property ['"](.*)['"] of (.*)/gmi,
				generateFingerprint: (matches) => `cannot-read-property-${matches[1]}-of-${matches[2]}`
			},
			{
				pattern: /Cannot read properties of (.*) \(reading ['"](.*)['"]\)/gmi,
				generateFingerprint: (matches) => `cannot-read-property-${matches[2]}-of-${matches[1]}`
			},
		];
		for (let i = 0; i < regexPatters.length; i++) {
			const match = regexPatters[i].pattern.exec(errorMessage);
			if (match) {
				const generatedFingerprint = regexPatters[i].generateFingerprint(match);
				return generatedFingerprint;
			}
		}
		errorMessage = errorMessage.trim().replace(/\s+/gm, "-");
		return errorMessage;
	}
}